import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';

// Types
export interface MembershipDetail {
  projectCode: string;
  code: string;
  status: string;
  type: string;
  title: string;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  paidMembership: number;
  membershipLevel: number;
  rewardPolicyOwner: string;
  rewardType: string; // NONE | STAMP | POINT
  rewardTitle?: string;
  settlementOwner?: string;
  validType?: number;
  validPeriod?: number;
  validAt?: string;
  stampSetCount?: number;
  voucherExchangeMethod?: string;
  voucherUseDelay?: number;
  pointRewardTarget?: string;
  pointExpireCycle?: string;
  pointRatio?: number;
  pointRoundingMode?: string;
  pointRoundingScale?: number;
  note?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface MembershipStats {
  totalMembers: number;
  activeMembers: number;
  newMembersThisMonth: number;
  totalStamps: number;
  totalPoints: number;
  stampRedemptions: number;
  pointRedemptions: number;
}

export interface MembershipSaveRequest {
  title: string;
  status: string;
  type: string;
  note?: string;
  rewardType: string;
  rewardPolicyOwner: string;
  paidMembership: number;
  membershipLevel: number;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  rewardTitle?: string;
  settlementOwner?: string;
  validType?: number;
  validPeriod?: number;
  validAt?: string;
  stampSetCount?: number;
  voucherExchangeMethod?: string;
  voucherUseDelay?: number;
  pointRewardTarget?: string;
  pointExpireCycle?: string;
  pointRatio?: number;
  pointRoundingMode?: string;
  pointRoundingScale?: number;
}

export type MembershipCreateRequest = MembershipSaveRequest;
export type MembershipUpdateRequest = MembershipSaveRequest;

export interface MembershipCreateResponse {
  success: boolean;
  message: string;
  data?: MembershipDetail | { data?: MembershipDetail; code?: string };
}

/** createMembership 응답에서 멤버십 코드 추출 (result.data?.code 또는 result.data?.data?.code) */
export const extractMembershipCodeFromCreateResponse = (result: MembershipCreateResponse): string | undefined => {
  const data = result.data as any;
  if (!data) return undefined;
  return data.code ?? data.data?.code;
};

// 멤버십-브랜드 매핑
/** GET /projects/{projectCode}/memberships/{membershipCode}/brands 응답의 brandMemberships 항목 */
export interface BrandMembership {
  projectCode: string;
  brandCode: string;
  membershipCode: string;
  membershipType?: string;
  /** 교환권 설정: { items: [{ voucherCode, brandCode?, stampCount }] } 형태 */
  voucherSettings?: { items?: Array<{ voucherCode?: string; brandCode?: string; stampCount?: number }> };
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface MembershipBrandsApiResponse {
  totalCount: number;
  brandMemberships: BrandMembership[];
}

export interface MembershipBrandsResponse {
  brandCodes: string[];
}

/** POST .../brands/sync 요청의 brands[] 항목 (voucherSettings 제외) */
export interface MembershipBrandSyncItem {
  brandCode: string;
  membershipCode: string;
  membershipType: string;
  rewardType: string;
  operation: 'ADD' | 'DELETE';
}

export interface MembershipBrandsSyncRequest {
  brands: MembershipBrandSyncItem[];
}

// 멤버십-교환권 매핑
export interface MembershipVoucherItem {
  voucherCode: string;
  brandCode: string;
  stampCount: number;
}

export interface MembershipVouchersResponse {
  items: MembershipVoucherItem[];
}

export interface MembershipVouchersSaveRequest {
  items: MembershipVoucherItem[];
}

/** 브랜드-멤버십 벌크 업데이트 한 건: 교환권설정정보는 한 브랜드에 여러 개일 수 있음 */
export interface BrandMembershipBulkUpdateItem {
  membershipCode: string;
  brandCode: string;
  voucherSettings: Array<{ voucherCode: string; stampCount: number }>;
}

export interface MembershipUpdateResponse {
  success: boolean;
  message: string;
  data?: MembershipDetail;
}

// 목록 조회 API 타입 - MembershipDetail을 확장
export interface MembershipListItem extends MembershipDetail {
  brandCount: number;
  note?: string;
}

export interface MembershipListResponse {
  timestamp: number;
  errors?: {
    error?: Array<{
      code: number;
      target: string;
      message: string;
    }>;
  };
  data: {
    totalCount: number;
    memberships: MembershipListItem[];
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

/** 멤버십 목록 조회 API 옵션 */
export interface GetMembershipListParams {
  status?: string;
  brandCode?: string;
  type?: string;
  rewardType?: string;
}

// 멤버십 목록 조회 API
export const getMembershipList = async (
  projectCode: string,
  page: number = 0,
  size: number = 20,
  params?: GetMembershipListParams
): Promise<{
  memberships: MembershipListItem[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      includeBrandCount: 'true',
    });
    if (params?.status) queryParams.append('status', params.status);
    if (params?.brandCode) queryParams.append('brandCode', params.brandCode);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.rewardType) queryParams.append('rewardType', params.rewardType);

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships?${queryParams}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result: MembershipListResponse = await response.json();

    if (result.errors?.error && result.errors.error.length > 0) {
      const errorMessage = result.errors.error.map((e) => e.message).join(', ');
      throw new Error(errorMessage);
    }

    return {
      memberships: result.data.memberships,
      totalCount: result.data.totalCount,
      totalPages: result.data.totalPages,
      currentPage: result.data.currentPage,
      pageSize: result.data.pageSize,
      hasNext: result.data.hasNext,
      hasPrevious: result.data.hasPrevious,
    };
  } catch (error: any) {
    console.error('멤버십 목록 조회 API 오류:', error);

    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }

    throw error;
  }
};

// API Functions
export const getMembershipDetail = async (
  projectCode: string,
  membershipCode: string
): Promise<MembershipDetail | null> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships/${membershipCode}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        }
      }
    );

    // 404 에러는 멤버십이 없는 것으로 처리하고 null 반환 (에러 로그 없이)
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data;
    
    return data;
  } catch (error: any) {
    // 404 에러는 이미 위에서 처리되었으므로 catch에서 재처리하지 않음
    // 404 관련 에러 메시지인 경우 에러를 던지지 않고 null 반환
    if (error?.message && (error.message.includes('404') || error.message.includes('status: 404'))) {
      return null;
    }
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    // 404가 아닌 다른 에러만 로깅하고 던짐
    console.error('멤버십 상세 조회 API 오류:', error);
    throw error;
  }
};

export const createMembership = async (
  projectCode: string,
  membershipData: MembershipCreateRequest
): Promise<MembershipCreateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        },
        body: JSON.stringify(membershipData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      // 에러 응답을 그대로 전달 (필드별 에러 처리용)
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      const error: any = new Error(errorMessage);
      error.status = statusCode;
      error.data = errorData;
      throw error;
    }

    const result = await response.json();
    return {
      success: true,
      message: '멤버십이 성공적으로 생성되었습니다.',
      data: result
    };
  } catch (error: any) {
    console.error('멤버십 생성 API 오류:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      (networkError as any).status = error.status;
      (networkError as any).data = error.data;
      throw networkError;
    }
    
    throw error;
  }
};

export const updateMembership = async (
  projectCode: string,
  membershipCode: string,
  membershipData: MembershipUpdateRequest
): Promise<MembershipUpdateResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships/${membershipCode}`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        },
        body: JSON.stringify(membershipData)
      }
    );

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      // 에러 응답을 그대로 전달 (필드별 에러 처리용)
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      const error: any = new Error(errorMessage);
      error.status = statusCode;
      error.data = errorData;
      throw error;
    }

    const result = await response.json();
    return {
      success: true,
      message: '멤버십이 성공적으로 수정되었습니다.',
      data: result
    };
  } catch (error: any) {
    console.error('멤버십 수정 API 오류:', error);
    
    // 네트워크 에러인 경우 사용자 친화적 메시지로 변환
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    
    throw error;
  }
};

/** GET .../memberships/{membershipCode}/brands 공통 조회 (브랜드 매핑·교환권 설정 탭 공용) */
export const getBrandMemberships = async (
  projectCode: string,
  membershipCode: string
): Promise<MembershipBrandsApiResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships/${membershipCode}/brands`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        }
      }
    );

    if (response.status === 404) {
      return { totalCount: 0, brandMemberships: [] };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data;
    const brandMemberships = Array.isArray(data?.brandMemberships) ? data.brandMemberships : [];
    const totalCount = typeof data?.totalCount === 'number' ? data.totalCount : brandMemberships.length;
    return {
      totalCount,
      brandMemberships
    };
  } catch (error: any) {
    console.error('브랜드 멤버십 조회 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

// 멤버십-브랜드 매핑 동기화 (ADD/DELETE 구분)
export const saveMembershipBrandsSync = async (
  projectCode: string,
  membershipCode: string,
  payload: MembershipBrandsSyncRequest
): Promise<{ success: boolean; message: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships/${membershipCode}/brands/sync`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || '브랜드 매핑이 저장되었습니다.'
    };
  } catch (error: any) {
    console.error('멤버십 브랜드 매핑 동기화 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

// 멤버십-교환권 매핑 조회
export const getMembershipVouchers = async (
  projectCode: string,
  membershipCode: string,
  signal?: AbortSignal
): Promise<MembershipVouchersResponse> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships/${membershipCode}/vouchers`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        },
        signal
      }
    );

    if (response.status === 404) {
      return { items: [] };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const data = result.data;
    return {
      items: data?.items ?? []
    };
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      throw error;
    }
    console.error('멤버십 교환권 매핑 조회 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

// 멤버십-교환권 매핑 저장
export const saveMembershipVouchers = async (
  projectCode: string,
  membershipCode: string,
  payload: MembershipVouchersSaveRequest
): Promise<{ success: boolean; message: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/memberships/${membershipCode}/vouchers`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || '교환권 설정이 저장되었습니다.'
    };
  } catch (error: any) {
    console.error('멤버십 교환권 매핑 저장 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

// 브랜드-멤버십 벌크 업데이트 (교환권 설정 일괄 저장)
export const brandMembershipsBulkUpdate = async (
  projectCode: string,
  payload: BrandMembershipBulkUpdateItem[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo) {
      throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
    }

    const response = await fetch(
      getApiV1Url(`/projects/${projectCode}/brand-memberships/bulk-update`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Account-Code': userInfo.accountCode,
          'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId
        },
        body: JSON.stringify({ brandMemberships: payload })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || '교환권 설정이 저장되었습니다.'
    };
  } catch (error: any) {
    console.error('브랜드-멤버십 벌크 업데이트 API 오류:', error);
    if (CommonUtils.isNetworkError(error)) {
      const networkError = new Error(CommonUtils.getNetworkErrorMessage(error));
      (networkError as any).isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
